import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, LogOut, Search, Bell, Plus, ChevronRight } from "lucide-react";
import { useAuth } from "@/features/auth/AuthProvider";
import { ClubLogo } from "@/components/brand/ClubLogo";
import { GlobalSearch, useGlobalSearch } from "@/components/search/GlobalSearch";
import { cn } from "@/lib/utils";

interface PortalLayoutProps {
  portalName: string;
  basePath: string;
  nav: {
    label: string;
    to: string;
    icon?: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
    section?: string;
    permission?: string;
  }[];
}

/**
 * Portal shell — occupies the viewport, sidebar is FIXED, main scrolls
 * independently. The sidebar never scrolls away with page content.
 *
 * Layout:
 *   <div h-dvh overflow-hidden>
 *     <aside fixed inset-y-0 left-0 w-72 (lg only)>
 *       <brand />
 *       <nav flex-1 overflow-y-auto />
 *       <user />
 *     </aside>
 *     <div h-dvh flex-col lg:pl-72>
 *       <header sticky top-0 />
 *       <main flex-1 overflow-y-auto />
 *     </div>
 *   </div>
 */
export function PortalLayout({ portalName, basePath, nav }: PortalLayoutProps) {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const search = useGlobalSearch();

  useEffect(() => {
    let meta = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "robots";
      document.head.appendChild(meta);
    }
    meta.content = "noindex, nofollow";
    return () => {
      meta?.remove();
    };
  }, []);
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const visibleNav = nav.filter((item) => !item.permission || can(item.permission));
  const sections = groupBySection(visibleNav);
  const activeSegment = location.pathname.split("/").filter(Boolean).slice(1)[0] ?? "";
  const activeItem = visibleNav.find((n) => n.to === activeSegment) ?? visibleNav[0];

  return (
    <div className="h-dvh overflow-hidden bg-off-white text-foreground">
      <GlobalSearch />

      {/* Sidebar — always fixed inside the viewport */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-40 flex w-72 flex-col bg-sidebar text-sidebar-foreground transition-transform lg:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full",
          "lg:z-30",
        )}
        aria-label={`${portalName} navigation`}
      >
        <div className="flex h-16 shrink-0 items-center gap-3 border-b border-sidebar-border px-4">
          <Link to="/" aria-label="Home">
            <ClubLogo variant="icon" priority className="h-11 w-11" />
          </Link>
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-gold">
              Portal
            </p>
            <p className="truncate text-display text-lg font-bold uppercase text-white">
              {portalName}
            </p>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          {Object.entries(sections).map(([section, items]) => (
            <div key={section} className="mb-4">
              {section !== "_" ? (
                <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-silver">
                  {section}
                </p>
              ) : null}
              <ul className="grid gap-0.5">
                {items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.to}>
                      <NavLink
                        to={`${basePath}${item.to ? `/${item.to}` : ""}`}
                        end={item.to === ""}
                        className={({ isActive }) =>
                          cn(
                            "flex items-center gap-2.5 rounded-sm px-3 py-2 text-sm font-medium text-silver-light transition-colors hover:bg-sidebar-accent hover:text-white",
                            isActive &&
                              "bg-sidebar-accent text-white shadow-[inset_3px_0_0_var(--gold)]",
                          )
                        }
                      >
                        {Icon ? <Icon aria-hidden className="size-4 text-gold" /> : null}
                        <span>{item.label}</span>
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
        {user ? (
          <div className="shrink-0 border-t border-sidebar-border p-3">
            <div className="flex items-center gap-3 rounded-sm bg-white/5 p-2.5">
              {user.profile.avatarUrl ? (
                <img
                  src={user.profile.avatarUrl}
                  alt=""
                  className="size-10 rounded-sm object-cover"
                />
              ) : (
                <div className="size-10 rounded-sm bg-gold/20" />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-white">{user.name}</p>
                <p className="truncate text-[11px] uppercase tracking-[0.14em] text-silver">
                  {user.roles[0]?.replace(/_/g, " ")}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              className="mt-2 inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-sm border border-white/10 px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-silver-light hover:border-gold hover:text-gold"
            >
              <LogOut aria-hidden className="size-3.5" /> Sign out
            </button>
          </div>
        ) : null}
      </aside>

      {open ? (
        <button
          type="button"
          aria-label="Close menu"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-30 bg-black/60 lg:hidden"
        />
      ) : null}

      {/* Main column — offset for the fixed sidebar on lg+ */}
      <div className="flex h-dvh flex-col overflow-hidden lg:pl-72">
        <header className="sticky top-0 z-20 flex h-14 shrink-0 items-center gap-2 border-b border-border bg-white px-3 sm:px-6">
          <button
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            className="grid size-10 place-items-center rounded-sm hover:bg-off-white lg:hidden"
          >
            {open ? <X aria-hidden /> : <Menu aria-hidden />}
          </button>
          <nav
            aria-label="Breadcrumb"
            className="hidden text-xs font-semibold uppercase tracking-[0.16em] text-slate-brand sm:flex sm:items-center sm:gap-1"
          >
            <span className="text-navy">{portalName}</span>
            <ChevronRight aria-hidden className="size-3 text-silver" />
            <span className="text-muted-foreground">{activeItem.label}</span>
          </nav>
          <div className="ml-auto flex items-center gap-1.5">
            <button
              type="button"
              onClick={search.open}
              className="hidden items-center gap-2 rounded-sm border border-input bg-off-white px-3 py-2 text-xs text-muted-foreground hover:border-navy md:inline-flex"
            >
              <Search aria-hidden className="size-4" /> Search
              <kbd className="ml-3 rounded-sm border border-border bg-white px-1.5 py-0.5 text-[10px] font-bold text-navy">
                ⌘K
              </kbd>
            </button>
            <button
              aria-label="Notifications"
              className="grid size-10 place-items-center rounded-sm hover:bg-off-white"
              type="button"
            >
              <Bell aria-hidden className="size-4" />
            </button>
            <Link
              to="/"
              className="hidden text-xs font-semibold uppercase tracking-[0.16em] text-navy hover:text-gold-dark lg:inline"
            >
              View site →
            </Link>
          </div>
        </header>
        <main id="main-content" className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function groupBySection(nav: PortalLayoutProps["nav"]) {
  return nav.reduce<Record<string, PortalLayoutProps["nav"]>>((acc, item) => {
    const key = item.section ?? "_";
    (acc[key] ??= []).push(item);
    return acc;
  }, {});
}
